import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import Layout from "@/components/Layout";
import DatePicker from "@/components/DatePicker";
import { api } from "@/lib/api";
import { fmt } from "@/lib/format";
import {
  Wallet,
  TrendingDown,
  ShieldAlert,
  Percent,
  Banknote,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";

const KPIS = [
  { key: "toplam_asil_alacak", label: "İcra Takibine Giren Tutar", icon: Banknote, tone: "neutral" },
  { key: "toplam_tahsilat", label: "Toplam Tahsilat", icon: Wallet, tone: "success" },
  { key: "toplam_faiz_tahsilat", label: "Toplam Faiz Tahsilatı", icon: Percent, tone: "interest" },
  { key: "toplam_brut_risk", label: "Brüt Risk", icon: ShieldAlert, tone: "danger" },
  { key: "toplam_net_risk", label: "Net Risk / Kâr", icon: ShieldAlert, tone: "danger" },
];

const toneColor = {
  neutral: "#26231E",
  success: "#2F6E4E",
  danger: "#A63A3A",
  interest: "#7C5E9B",
};

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [threshold, setThreshold] = useState(50000);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const params = {};
    if (start) params.start = start;
    if (end) params.end = end;
    const { data } = await api.get("/dashboard/summary", { params });
    setSummary(data);
  }, [start, end]);

  useEffect(() => {
    load();
  }, [load]);

  const totals = summary?.totals || {};
  const bars = summary?.status_bars || {};
  const customers = (summary?.by_customer || []).slice(0, 8);
  const allCustomers = summary?.by_customer || [];
  const riskyCustomers = allCustomers
    .filter((c) => c.net_risk >= Number(threshold || 0))
    .sort((a, b) => b.net_risk - a.net_risk);
  // İcra "Kalan" = İcra Takibine Giren Tutar − Toplam Tahsilat
  const icraKalanCalc = Math.max((totals.toplam_asil_alacak || 0) - (totals.toplam_tahsilat || 0), 0);
  const pieData = [
    { name: "Tahsilat", value: Math.max(totals.toplam_tahsilat || 0, 0) },
    { name: "Kalan", value: icraKalanCalc },
  ];
  const PIE_COLORS = ["#2F6E4E", "#B8933F"];

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">İCRA TAKİP</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">İcra Gösterge Paneli</h1>
        </div>
        <div className="flex items-end gap-3">
          <div className="w-[170px]">
            <label className="kicker block mb-1.5">BAŞLANGIÇ</label>
            <DatePicker value={start} onChange={setStart} placeholder="Başlangıç" testId="dash-filter-start" />
          </div>
          <div className="w-[170px]">
            <label className="kicker block mb-1.5">BİTİŞ</label>
            <DatePicker value={end} onChange={setEnd} placeholder="Bitiş" testId="dash-filter-end" />
          </div>
        </div>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        {KPIS.map((kpi) => {
          const Icon = kpi.icon;
          const val = totals[kpi.key];
          let color = toneColor[kpi.tone];
          if (kpi.key === "toplam_net_risk" && (val || 0) < 0) color = toneColor.success;
          return (
            <div
              key={kpi.key}
              className="bg-white border border-[#E7DECC] rounded-sm p-4"
              data-testid={`kpi-${kpi.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="kicker">{kpi.label}</span>
                <Icon className="w-4 h-4" style={{ color }} />
              </div>
              <div
                className="font-heading font-black text-xl tabular-nums tracking-tight"
                style={{ color }}
              >
                {fmt(val)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Durum Barları: İnfaz / Devam / Hareketsiz */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatusBar label="İnfaz" adet={bars.infaz?.adet ?? 0} sf={bars.infaz?.sf ?? 0} tahsilat={bars.infaz?.tahsilat ?? 0} color="#2F6E4E" testId="bar-infaz" />
        <StatusBar label="Devam" adet={bars.devam?.adet ?? 0} sf={bars.devam?.sf ?? 0} color="#B8933F" testId="bar-devam" />
        <StatusBar label="Hareketsiz" adet={bars.hareketsiz?.adet ?? 0} sf={bars.hareketsiz?.sf ?? 0} color="#A79A86" testId="bar-hareketsiz" />
      </div>

      {/* Risk Alerts */}
      <div className="bg-white border border-[#E7DECC] rounded-sm mb-6" data-testid="risk-alerts">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-[#E7DECC]">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-[#A63A3A]" />
            <span className="font-heading font-bold text-sm tracking-tight">Yüksek Risk Uyarıları</span>
            <span className="px-2 py-0.5 rounded-sm bg-[#A63A3A]/10 text-[#A63A3A] text-xs font-semibold">
              {riskyCustomers.length}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="kicker">EŞİK (NET RİSK ₺)</span>
            <input
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              data-testid="risk-threshold-input"
              className="w-32 px-3 py-1.5 border border-[#E7DECC] rounded-sm tabular-nums text-sm focus:outline-none focus:border-[#B8933F]"
            />
          </div>
        </div>
        <div className="p-4">
          {riskyCustomers.length === 0 ? (
            <div className="text-sm text-[#8C8377] py-4 text-center" data-testid="risk-empty">
              Belirlenen eşiği aşan müşteri yok.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {riskyCustomers.map((c) => (
                <button
                  key={c.musteri}
                  onClick={() => navigate(`/cari/${encodeURIComponent(c.musteri)}`)}
                  data-testid={`risk-badge-${c.musteri}`}
                  className="flex items-center gap-2 px-3 py-2 bg-[#A63A3A]/10 border border-[#A63A3A]/30 rounded-sm hover:bg-[#A63A3A]/20 transition-colors duration-150"
                >
                  <span className="w-2 h-2 rounded-full bg-[#A63A3A]" />
                  <span className="text-sm font-medium text-[#26231E]">{c.musteri}</span>
                  <span className="text-sm font-bold tabular-nums text-[#A63A3A]">{fmt(c.net_risk)}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="chart-by-customer">
          <div className="kicker mb-4">MÜŞTERİ BAZLI RİSK & TAHSİLAT</div>
          {customers.length === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-[#8C8377] text-sm">
              Grafik için veri yok. Cari hesaplar ekleyin.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={customers} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7DECC" vertical={false} />
                <XAxis dataKey="musteri" tick={{ fontSize: 11, fill: "#8C8377" }} />
                <YAxis tick={{ fontSize: 11, fill: "#8C8377" }} width={70} />
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E7DECC" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="tahsilat" name="Tahsilat" fill="#2F6E4E" />
                <Bar dataKey="brut_risk" name="Brüt Risk" fill="#A63A3A" />
                <Bar dataKey="net_risk" name="Net Risk" fill="#B8933F" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid="chart-pie">
          <div className="kicker mb-4">TAHSİLAT / KALAN DAĞILIMI</div>
          {(totals.toplam_tahsilat || 0) + icraKalanCalc === 0 ? (
            <div className="h-[300px] flex items-center justify-center text-[#8C8377] text-sm">
              Dağılım için veri yok.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={PIE_COLORS[i]} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => fmt(v)} contentStyle={{ borderRadius: 2, border: "1px solid #E7DECC" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <button
        onClick={() => navigate("/kayitlar")}
        data-testid="go-records-button"
        className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150"
      >
        Cari Hesapları Yönet <ArrowRight className="w-4 h-4" />
      </button>
    </Layout>
  );
}

function StatusBar({ label, adet, sf, tahsilat, color, testId }) {
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="kicker">{label}</span>
        <span
          className="text-xs font-semibold px-2 py-0.5 rounded-sm tabular-nums"
          style={{ backgroundColor: color + "1A", color }}
          data-testid={`${testId}-adet`}
        >
          {adet} dosya
        </span>
      </div>
      <div className="font-heading font-bold text-lg tabular-nums" style={{ color }} data-testid={`${testId}-sf`}>
        {fmt(sf)}
      </div>
      <div className="text-xs text-[#8C8377] mt-0.5">Toplam SF</div>
      {tahsilat != null && (
        <div className="mt-2 pt-2 border-t border-[#EFE7D6]">
          <div className="font-heading font-bold text-lg tabular-nums text-[#2F6E4E]" data-testid={`${testId}-tahsilat`}>{fmt(tahsilat)}</div>
          <div className="text-xs text-[#8C8377] mt-0.5">Toplam Tahsilat</div>
        </div>
      )}
    </div>
  );
}
